import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, Tag, CheckCircle, XCircle, MapPin, Clock } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { formatPrice } from "../utils/format";
import { validateCoupon } from "../utils/coupons";
import { getShippingSettings, DEFAULT_SHIPPING_SETTINGS } from "../utils/shipping";
import ProductCard from "../components/shop/ProductCard";
import toast from "react-hot-toast";

export default function CartPage() {
  const { cart, total, removeFromCart, updateQty } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [couponCode, setCouponCode] = useState("");
  const [couponState, setCouponState] = useState(null); // { valid, discount, coupon, error }
  const [couponLoading, setCouponLoading] = useState(false);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [shippingSettings, setShippingSettings] = useState(DEFAULT_SHIPPING_SETTINGS);

  const discount = couponState?.valid ? couponState.discount : 0;
  const freeThreshold = shippingSettings.freeShippingThreshold || 50000;
  const isFree = shippingSettings.enableFreeShipping && total >= freeThreshold;
  const shipping = isFree ? 0 : (shippingSettings.defaultDeliveryFee || 3000);
  const grandTotal = Math.max(0, total - discount + shipping);

  useEffect(() => {
    getShippingSettings().then(setShippingSettings).catch(console.warn);
  }, []);

  // Load recently viewed
  useEffect(() => {
    const ids = JSON.parse(localStorage.getItem("afriport_recently_viewed") || "[]").slice(0, 4);
    if (!ids.length) return;
    Promise.all(ids.map(id => getDoc(doc(db, "products", id)).then(s => s.exists() ? { id: s.id, ...s.data() } : null)))
      .then(prods => setRecentlyViewed(prods.filter(Boolean)));
  }, []);

  async function applyCoupon() {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    const result = await validateCoupon(couponCode, total);
    setCouponState(result);
    if (result.valid) toast.success(`Coupon applied! You save ${formatPrice(result.discount)}`);
    else toast.error(result.error);
    setCouponLoading(false);
  }

  function removeCoupon() {
    setCouponState(null);
    setCouponCode("");
    toast.success("Coupon removed");
  }

  if (cart.length === 0) {
    return (
      <div style={{ minHeight: "60vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 20 }}>
        <div style={{ width: 80, height: 80, background: "var(--brand-light)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <ShoppingBag size={36} color="var(--brand)" />
        </div>
        <h2 style={{ fontFamily: "var(--font-head)", fontSize: 24, fontWeight: 700 }}>Your cart is empty</h2>
        <p style={{ color: "var(--text-muted)" }}>Start adding items from our shop.</p>
        <Link to="/shop" className="btn btn-primary">Browse Products</Link>
        {recentlyViewed.length > 0 && (
          <div style={{ marginTop: 40, width: "100%", maxWidth: 900, padding: "0 24px" }}>
            <h3 style={{ fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 18, marginBottom: 20, textAlign: "center" }}>Recently Viewed</h3>
            <div className="product-grid">
              {recentlyViewed.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ background: "var(--surface)", minHeight: "100vh" }}>
      <div className="container section-sm">
        <h1 style={{ fontFamily: "var(--font-head)", fontSize: 28, fontWeight: 700, marginBottom: 32 }}>
          Shopping Cart <span style={{ fontSize: 16, color: "var(--text-muted)", fontWeight: 400 }}>({cart.length} item{cart.length !== 1 ? "s" : ""})</span>
        </h1>

        <div className="cart-layout-grid">
          {/* Cart items */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {cart.map(item => (
              <div key={item.id + JSON.stringify(item.variant)} className="card" style={{ padding: "clamp(14px, 3vw, 20px)", display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
                <Link to={`/product/${item.id}`}>
                  <div style={{ width: 80, height: 80, borderRadius: 10, overflow: "hidden", background: "var(--surface)", flexShrink: 0, border: "1px solid var(--border)" }}>
                    {item.image
                      ? <img src={item.image} alt={item.name} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>📦</div>}
                  </div>
                </Link>
                <div style={{ flex: "1 1 200px", minWidth: 0 }}>
                  <Link to={`/product/${item.id}`} style={{ fontWeight: 600, fontSize: 15, color: "var(--text)", display: "block", marginBottom: 2 }}>
                    {item.name}
                  </Link>
                  <p style={{ fontSize: 13, color: "var(--text-muted)" }}>{item.category}</p>
                  {item.variant && Object.keys(item.variant).length > 0 && (
                    <p style={{ fontSize: 12, color: "var(--accent)", marginTop: 2 }}>
                      {Object.entries(item.variant).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                    </p>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", justifyContent: "space-between", minWidth: 180 }}>
                  <div style={{ display: "flex", alignItems: "center", border: "1px solid var(--border)", borderRadius: 8 }}>
                    <button onClick={() => updateQty(item.id, item.qty - 1)}
                      style={{ width: 32, height: 32, border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Minus size={13} />
                    </button>
                    <span style={{ width: 32, textAlign: "center", fontSize: 14, fontWeight: 600 }}>{item.qty}</span>
                    <button onClick={() => updateQty(item.id, item.qty + 1)}
                      style={{ width: 32, height: 32, border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Plus size={13} />
                    </button>
                  </div>
                  <div style={{ minWidth: 100, textAlign: "right" }}>
                    <div style={{ fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 15 }}>{formatPrice(item.price * item.qty)}</div>
                    {item.qty > 1 && <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{formatPrice(item.price)} each</div>}
                  </div>
                  <button onClick={() => removeFromCart(item.id)}
                    style={{ border: "none", background: "transparent", cursor: "pointer", color: "var(--danger)", padding: 4, display: "flex" }}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="card" style={{ padding: 24, position: "sticky", top: 90 }}>
            <h3 style={{ fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 18, marginBottom: 20 }}>Order Summary</h3>

            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
                <span style={{ color: "var(--text-muted)" }}>Subtotal</span>
                <span style={{ fontWeight: 500 }}>{formatPrice(total)}</span>
              </div>
              {discount > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
                  <span style={{ color: "var(--success)" }}>Coupon Discount</span>
                  <span style={{ fontWeight: 500, color: "var(--success)" }}>-{formatPrice(discount)}</span>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
                <span style={{ color: "var(--text-muted)" }}>Estimated Shipping</span>
                <span style={{ fontWeight: 600, color: shipping === 0 ? "var(--success)" : "var(--text)" }}>
                  {shipping === 0 ? "Free 🎉" : formatPrice(shipping)}
                </span>
              </div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", background: "var(--surface)", padding: "10px 12px", borderRadius: 8 }}>
                {shipping > 0 && total < freeThreshold ? (
                  <p style={{ margin: "0 0 4px", color: "var(--brand)", fontWeight: 600 }}>
                    Add {formatPrice(freeThreshold - total)} more for free door delivery!
                  </p>
                ) : null}
                <div style={{ display: "flex", alignItems: "center", gap: 6, margin: "2px 0 0" }}>
                  <MapPin size={12} color="var(--brand)" />
                  <span>Door Delivery & Station Pickup options available at checkout.</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, color: "var(--success)", fontWeight: 600 }}>
                  <Clock size={12} />
                  <span>Guaranteed delivery within 7 days of payment.</span>
                </div>
              </div>
            </div>

            {/* Coupon */}
            {!couponState?.valid ? (
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: "flex", gap: 8 }}>
                  <div style={{ position: "relative", flex: 1 }}>
                    <Tag size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
                    <input className={`form-input${couponState && !couponState.valid ? " coupon-invalid" : ""}`}
                      placeholder="Coupon code" style={{ paddingLeft: 30, fontSize: 13 }}
                      value={couponCode} onChange={e => setCouponCode(e.target.value.toUpperCase())}
                      onKeyDown={e => e.key === "Enter" && applyCoupon()} />
                  </div>
                  <button onClick={applyCoupon} disabled={couponLoading || !couponCode.trim()} className="btn btn-secondary btn-sm">
                    {couponLoading ? "…" : "Apply"}
                  </button>
                </div>
                {couponState && !couponState.valid && (
                  <p style={{ fontSize: 12, color: "var(--danger)", marginTop: 6, display: "flex", alignItems: "center", gap: 4 }}>
                    <XCircle size={12} /> {couponState.error}
                  </p>
                )}
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", background: "#F0FDF4", border: "1px solid #86EFAC", borderRadius: 8, marginBottom: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--success)", fontWeight: 500 }}>
                  <CheckCircle size={14} /> {couponState.coupon.code} — Save {formatPrice(discount)}
                </div>
                <button onClick={removeCoupon} style={{ border: "none", background: "transparent", cursor: "pointer", color: "var(--text-muted)", fontSize: 12 }}>Remove</button>
              </div>
            )}

            <div style={{ borderTop: "1px solid var(--border)", paddingTop: 16, marginBottom: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontWeight: 700, fontSize: 16 }}>Total</span>
                <span style={{ fontFamily: "var(--font-head)", fontWeight: 800, fontSize: 22, color: "var(--brand)" }}>{formatPrice(grandTotal)}</span>
              </div>
            </div>

            <button
              onClick={() => user
                ? navigate("/checkout", { state: { coupon: couponState?.valid ? couponState : null } })
                : navigate("/login", { state: { from: "/checkout" } })}
              className="btn btn-primary btn-lg w-full">
              {user ? "Proceed to Checkout" : "Sign in to Checkout"} <ArrowRight size={18} />
            </button>

            <Link to="/shop" style={{ display: "block", textAlign: "center", marginTop: 14, fontSize: 14, color: "var(--text-muted)" }}>
              ← Continue Shopping
            </Link>
          </div>
        </div>

        {/* Recently viewed */}
        {recentlyViewed.length > 0 && (
          <div style={{ marginTop: 56 }}>
            <h2 style={{ fontFamily: "var(--font-head)", fontSize: 22, fontWeight: 700, marginBottom: 24 }}>Recently Viewed</h2>
            <div className="product-grid">
              {recentlyViewed.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
